import { pluralizeLastWord } from '@gonfalon/format/pluralizeLastWord';
import { toJS } from '@gonfalon/legacy-immutable/immutableUtils';
import { msg } from '@gonfalon/messages/msg';

import type { RuleLabel } from '#utils/flags/flagTypes';
import { replaceRuleLabelText } from '#utils/flags/replaceRuleLabelText';

import { AutomatedReleaseInstructionKind } from '../automated-release/types';
import { ClauseInstructionKind } from '../clauses/constants';
import { MembersAndTeamsInstructionKind } from '../membersAndTeams/types';
import { OnOffInstructionKind } from '../onOff/types';
import { FlagPrerequisitesInstructionKind } from '../prerequisites/types';
import { RuleInstructionKind } from '../rules/constants';
import type { AddRuleSemanticInstruction } from '../rules/types';
import { hasEmptyTargets } from '../targets/helpers';
import { TargetsInstructionKind } from '../targets/types';
import { hasEmptyUserTargets } from '../userTargets/helpers';
import { UserTargetsInstructionKind } from '../userTargets/types';
import type { VariationSemanticInstruction } from '../variations/types';

import { instructionKindToDescription } from './constants';
import { getActionVerb } from './getActionVerb';
import type { SemanticInstruction } from './types';

export const getInstructionDescriptionText = (
  instruction: SemanticInstruction | VariationSemanticInstruction,
  ruleLabel: RuleLabel,
) => {
  let text = instructionKindToDescription[instruction.kind];
  text = replaceRuleLabelText(text, ruleLabel);
  if (!text) {
    return msg('flags.changeSummary.updateKind', { kind: instruction.kind });
  }

  if (
    instruction.kind === MembersAndTeamsInstructionKind.ADD_MEMBERS ||
    instruction.kind === MembersAndTeamsInstructionKind.REMOVE_MEMBERS
  ) {
    return pluralizeLastWord(text, instruction.values.length);
  }

  if (
    instruction.kind === UserTargetsInstructionKind.ADD_USER_TARGETS ||
    instruction.kind === UserTargetsInstructionKind.REMOVE_USER_TARGETS ||
    instruction.kind === TargetsInstructionKind.ADD_TARGETS ||
    instruction.kind === TargetsInstructionKind.REMOVE_TARGETS
  ) {
    return pluralizeLastWord(text, toJS(instruction.values).length);
  }
  if (instruction.kind === ClauseInstructionKind.REMOVE_CLAUSES) {
    return pluralizeLastWord(text, instruction.clauseIds.length);
  }
  if (instruction.kind === ClauseInstructionKind.ADD_CLAUSES) {
    return pluralizeLastWord(text, instruction.clauses.length);
  }

  if (
    (instruction.kind === RuleInstructionKind.ADD_RULE ||
      instruction.kind === RuleInstructionKind.UPDATE_RULE_VARIATION_OR_ROLLOUT) &&
    (instruction as AddRuleSemanticInstruction).experimentAllocation
  ) {
    const [pref] = getActionVerb(text);
    return msg('flags.changeSummary.experimentRule', { verb: pref, ruleLabel });
  }

  if (instruction.kind === RuleInstructionKind.ADD_RULE && instruction.progressiveRolloutConfiguration) {
    return msg('flags.changeSummary.addRuleProgressiveRollout');
  }

  if (instruction.kind === RuleInstructionKind.ADD_RULE && instruction.disabled) {
    return msg('flags.changeSummary.addDisabledRule', { ruleLabel });
  }

  if (
    (instruction.kind === RuleInstructionKind.UPDATE_RULE_VARIATION_OR_ROLLOUT ||
      instruction.kind === OnOffInstructionKind.UPDATE_FALLTHROUGH_VARIATION_OR_ROLLOUT) &&
    instruction.progressiveRolloutConfiguration
  ) {
    return msg('flags.changeSummary.updateProgressiveRollout');
  }

  if (
    instruction.kind === OnOffInstructionKind.UPDATE_FALLTHROUGH_VARIATION_OR_ROLLOUT &&
    instruction.experimentAllocation
  ) {
    return msg('flags.changeSummary.experimentServing', { text });
  }

  if (
    instruction.kind === FlagPrerequisitesInstructionKind.REPLACE_PREREQUISITES &&
    instruction.prerequisites.length === 0
  ) {
    return msg('flags.changeSummary.allPrerequisitesRemoved');
  }
  if (instruction.kind === RuleInstructionKind.REPLACE_RULES && instruction.rules.length === 0) {
    return msg('flags.changeSummary.allRulesRemoved', { ruleLabel });
  }

  if (instruction.kind === AutomatedReleaseInstructionKind.STOP_AUTOMATED_RELEASE) {
    if (instruction.finalVariationId === undefined) {
      return msg('flags.changeSummary.stopAutomatedRelease');
    }
    return instruction.ruleId !== undefined
      ? msg('flags.changeSummary.stopAutomatedReleaseSetRule', { ruleLabel })
      : msg('flags.changeSummary.stopAutomatedReleaseSetDefault');
  }
  if (instruction.kind === UserTargetsInstructionKind.REPLACE_USER_TARGETS && hasEmptyUserTargets(instruction)) {
    return msg('flags.changeSummary.allUsersRemoved');
  }

  if (instruction.kind === TargetsInstructionKind.REPLACE_TARGETS && hasEmptyTargets(instruction)) {
    return msg('flags.changeSummary.allTargetsRemoved', { contextKind: instruction.contextKind });
  }

  return text;
};
